/**
 * Issue contractor workflow. Mirrors the API contract (IssueWorkflowService,
 * ProjectModel.isIssueWorkflowEnabled):
 *  - switched on per project with
 *    source_entity_extra_attributes.issue_workflow_enabled === true. Strictly
 *    opt-in: there is no org-wide default, and a project without the flag keeps
 *    the plain open / closed issue behaviour;
 *  - the one field is issue.status:
 *    open -> sent_to_contractor -> responded, closed from any of them (reopen
 *    -> open, or sent_to_contractor when the issue already had recipients).
 *    Every status but closed counts as open, so a project without the
 *    flag only ever sees open / closed. The stage is simply the status when
 *    it is neither open nor closed;
 *  - the actions a user may take come from GET /v1/workflow/next-state (send
 *    from any status but closed, respond / forward from any status but closed (a
 *    contractor-inspector gets them on any open issue of its project), close
 *    from any status but closed (project manager / org admin, project owner,
 *    reviewer, or the inspector who created the issue), reopen from closed — the API decides; the panel also
 *    drops an offered action that does not fit the issue's status, see
 *    issueWorkflowActionsFor), their inputs from that action's `formInputs`,
 *    the history from the lineage.
 *
 * Everything here is pure. The functions that take `enabled` are the single
 * place the web decides between the two project modes, so a surface never
 * re-derives it.
 */

export const ISSUE_WORKFLOW = 'issue';

export const ISSUE_STATUS_OPEN = 'open';
export const ISSUE_STATUS_CLOSED = 'closed';

/** The open / closed grouping of an issue status (lists, tiles, counts). */
export type IssueStatusGroup = 'open' | 'closed';

/** The slice of a project payload the workflow switch is read from. */
export interface IssueWorkflowProject {
  id?: string | null;
  source_entity_extra_attributes?: Record<string, unknown> | null;
  sites?: { id?: string | null }[] | null;
}

/** A person an issue was assigned to (sent or forwarded, in API terms), or can be. */
export interface IssueWorkflowPerson {
  id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role_name?: string | null;
  sub_contractor_id?: string | null;
  sub_contractor_name?: string | null;
  prime?: boolean | null;
  sent_at?: string | null;
  sent_by_name?: string | null;
  forwarded_at?: string | null;
  forwarded_by_name?: string | null;
}

/** The slice of an issue payload the workflow reads, whichever fetch it came from. */
export interface IssueWorkflowIssue {
  id?: string | number | null;
  uuid?: string | null;
  status?: string | null;
  internal?: boolean | null;
  comment?: string | null;
  closed_at?: string | null;
  closedBy?: unknown;
  createdBy?: unknown;
  updated_at?: string | null;
  entity_type?: string | null;
  entity_id?: string | number | null;
  entity?: { id?: string | number | null } | null;
  project?: IssueWorkflowProject | null;
  project_id?: string | number | null;
  projectId?: string | null;
  contractor_accounts?: IssueWorkflowPerson[] | null;
  sub_accounts?: IssueWorkflowPerson[] | null;
}

export interface WorkflowInputOption {
  value: string;
  label: string;
}

/** One entry of a workflow state's `formInputs` (next-state / lineage). */
export interface WorkflowFormInput {
  name: string;
  type?: string;
  label?: string;
  required?: boolean;
  options?: { value: unknown; label?: string }[] | null;
}

/** One action of GET /v1/workflow/next-state. */
export interface WorkflowNextAction {
  state: string;
  wf?: string;
  ex?: string;
  options?: {
    label?: string;
    styles?: { color?: string } | null;
    formInputs?: WorkflowFormInput[] | null;
  } | null;
}

/** The responses of the issue workflow endpoints. */
export interface IssueResponse {
  data?: IssueWorkflowIssue;
  message?: string;
}

export interface IssueWorkflowActionsResponse {
  data?: WorkflowNextAction[];
}

export interface IssueContractorUsersResponse {
  data?: IssueWorkflowPerson[];
}

export interface IssueWorkflowHistoryResponse {
  data?: WorkflowLineageStep[];
}

/** One step of GET /v1/workflow/lineage?include=actor,state,attachments. */
export interface WorkflowLineageStep {
  id?: string;
  created_at?: string | null;
  input?: { formValues?: Record<string, unknown> | string | null; description?: string | null } | null;
  output?: Record<string, unknown> | null;
  state?: {
    name?: string;
    comment?: string | null;
    params?: { label?: string; formInputs?: WorkflowFormInput[] | null } | null;
  } | null;
  actor?: { first_name?: string | null; last_name?: string | null; email?: string | null } | null;
  attachments?: WorkflowAttachment[] | null;
}

/** An uploaded file as the API returns it (lineage attachments, action uploads). */
export interface WorkflowAttachment {
  id?: string;
  thumb_url?: string | null;
  original_url?: string | null;
  original_name?: string | null;
  mime?: string | null;
  fileType?: string;
  tags?: string[] | null;
}

/** A row of the Discussion timeline (app-workflow-discussion). */
export interface WorkflowDiscussionEntry {
  action: string;
  state_name: string;
  actor_name: string;
  actor_initials: string;
  date: string | null;
  notes: string | null;
  attachments: WorkflowAttachment[];
  forwarded_to: string[];
  /** The choices made on the action (its select inputs), labelled by the API. */
  details?: { label: string; value: string }[];
}

export type IssueStatusTone = 'pending' | 'done' | 'neutral';

/** The status pill of an issue: its status, the wording and the colour. */
export interface IssueStatusChip {
  status: string;
  label: string;
  tone: IssueStatusTone;
}

export type WorkflowInputKind = 'choice' | 'multi-choice' | 'files' | 'text' | 'unsupported';

/**
 * Status pills. Same colours as the punch list item status chips: grey for
 * open, amber while the contractor has it, green once closed. The
 * API has no label for a status. A status that is not listed still gets a
 * pill, see issueStatusChip.
 */
const ISSUE_STATUS_CHIPS: Record<string, { label: string; tone: IssueStatusTone }> = {
  [ISSUE_STATUS_OPEN]: { label: 'Open', tone: 'neutral' },
  sent_to_contractor: { label: 'Forwarded', tone: 'pending' },
  responded: { label: 'Responded', tone: 'pending' },
  [ISSUE_STATUS_CLOSED]: { label: 'Closed', tone: 'done' },
};

/** True when the project has the contractor workflow switched on. */
export function isIssueWorkflowEnabled(project: IssueWorkflowProject | null | undefined): boolean {
  return project?.source_entity_extra_attributes?.issue_workflow_enabled === true;
}

/** True for a closed issue status. */
export function isIssueStatusClosed(status: string | null | undefined): boolean {
  return status === ISSUE_STATUS_CLOSED;
}

/**
 * True for an open issue status: every status but closed. A missing status
 * counts as open, as the legacy screens always took it.
 */
export function isIssueStatusOpen(status: string | null | undefined): boolean {
  return !isIssueStatusClosed(status);
}

/** Where a status falls when issues are split, filtered or counted as open / closed. */
export function issueStatusGroup(status: string | null | undefined): IssueStatusGroup {
  return isIssueStatusClosed(status) ? ISSUE_STATUS_CLOSED : ISSUE_STATUS_OPEN;
}

/** True for a status that is a contractor-workflow stage: neither open nor closed. */
export function isIssueStageStatus(status: string | null | undefined): boolean {
  return !!status && status !== ISSUE_STATUS_OPEN && status !== ISSUE_STATUS_CLOSED;
}

/**
 * The contractor-workflow stage of an issue, or null when it has none: the
 * status when that is neither open nor closed. Only a workflow project ever
 * has one; a project without the flag only ever has open / closed.
 */
export function issueStage(issue: IssueWorkflowIssue | null | undefined): string | null {
  const status = issue?.status;
  return isIssueStageStatus(status) ? status : null;
}

/** The wording of an issue status: Open, Forwarded (sent_to_contractor), Responded, Closed. */
export function issueStatusLabel(status: string | null | undefined): string {
  return ISSUE_STATUS_CHIPS[status]?.label || humanizeWorkflowValue(status);
}

/**
 * An issue status as the plain badges print it: open / closed as they always
 * were (the raw value), a contractor-workflow stage in words.
 */
export function issueStatusText(status: string | null | undefined): string {
  return isIssueStageStatus(status) ? issueStatusLabel(status) : status || '';
}

/** The workflow states of an issue (GET /v1/workflow/next-state `state`). */
export const ISSUE_WORKFLOW_STATES = {
  send: 'issue-send-contractor',
  respond: 'issue-respond',
  forward: 'issue-forward-sub',
  close: 'issue-close',
  reopen: 'issue-reopen',
} as const;

/**
 * The statuses each workflow action can run from: send (Forward) from any
 * status but closed — the PM can forward again any time before the close,
 * adding recipients (API migration 20261004150000); respond /
 * forward from any status but closed (a contractor-inspector may respond to or
 * forward any open issue of its project, not only one forwarded to it — who
 * gets them on which status is the API's next-state call); close from any
 * status but closed; reopen from closed.
 */
const ISSUE_WORKFLOW_ACTION_GATES: Record<string, (status: string) => boolean> = {
  [ISSUE_WORKFLOW_STATES.send]: status => !isIssueStatusClosed(status),
  [ISSUE_WORKFLOW_STATES.respond]: status => !isIssueStatusClosed(status),
  [ISSUE_WORKFLOW_STATES.forward]: status => !isIssueStatusClosed(status),
  [ISSUE_WORKFLOW_STATES.close]: status => !isIssueStatusClosed(status),
  [ISSUE_WORKFLOW_STATES.reopen]: status => isIssueStatusClosed(status),
};

/**
 * Whether an action the API offers fits the issue's status. A state this list
 * does not know, or an issue whose status is not known yet, is left to the
 * API.
 */
export function isIssueWorkflowActionAllowed(state: string | null | undefined, status: string | null | undefined): boolean {
  const gate = state ? ISSUE_WORKFLOW_ACTION_GATES[state] : undefined;
  return !gate || !status || gate(status);
}

/** The offered actions that fit the issue's status, in the API's order. */
export function issueWorkflowActionsFor<T extends { state?: string | null }>(
  actions: T[] | null | undefined,
  status: string | null | undefined
): T[] {
  return (actions || []).filter(action => isIssueWorkflowActionAllowed(action?.state, status));
}

/** The issue's id as the API wants it: the uuid when the payload has both. */
export function issueWorkflowId(issue: IssueWorkflowIssue | null | undefined): string | null {
  const id = issue?.uuid || issue?.id;
  return id === undefined || id === null || id === '' ? null : String(id);
}

/** Execution id of an issue's workflow: where its lineage (history) lives. */
export function issueWorkflowExecutionId(issueId: string): string {
  return `${ISSUE_WORKFLOW}/${issueId}`;
}

/**
 * The project of the page an issue is shown on, from the app url:
 * /projects/<id>/... (project and site screens: daily report, consolidated
 * report, maps) and /issues/project/<id> (the Issues page). Null anywhere
 * else — search results span projects and say which project each issue is in.
 *
 * This is the context for issue payloads that do not name their project at
 * all: the daily report's own issues carry no project, no project id and no
 * scope entity.
 */
export function issuePageProjectId(url: string | null | undefined): string | null {
  const segments = String(url || '').split(/[?#]/)[0].split('/').filter(Boolean);
  if (segments[0] === 'projects' && segments[1]) {
    return segments[1];
  }
  if (segments[0] === 'issues' && segments[1] === 'project' && segments[2]) {
    return segments[2];
  }
  return null;
}

/**
 * Whether `issue` belongs to `project`. An issue payload names its project in
 * different ways depending on the fetch: `project`, `project_id`, only its
 * scope (the project itself or one of its sites), or not at all.
 * `contextProjectId` is for the last kind: the project of the page showing
 * the issue (see issuePageProjectId), or of the per-project fetch it came from.
 */
export function issueBelongsToProject(
  issue: IssueWorkflowIssue | null | undefined,
  project: IssueWorkflowProject | null | undefined,
  contextProjectId?: string | null
): boolean {
  const projectId = project?.id;
  if (!issue || !projectId) {
    return false;
  }
  // Only a uuid names the project: a numeric project_id is the database id.
  const named = [issue.project?.id, issue.project_id, issue.projectId, contextProjectId]
    .find(id => typeof id === 'string' && id !== '');
  if (named) {
    return named === projectId;
  }
  const scopeId = issue.entity?.id ?? issue.entity_id;
  if (scopeId === undefined || scopeId === null || scopeId === '') {
    return false;
  }
  if (issue.entity_type === 'project') {
    return String(scopeId) === projectId;
  }
  if (issue.entity_type === 'site') {
    return (project.sites || []).some(site => !!site?.id && site.id === String(scopeId));
  }
  return false;
}

/**
 * The project payload that decides the issue's mode, or null when nothing on
 * hand says which project the issue is in — which reads as "not enabled", so
 * an unknown project always gets the plain open / closed behaviour.
 *
 * `storeProject` is the project the screen has loaded (the store keeps
 * whichever project was fetched last, and nothing while a fetch is in
 * flight); it only counts when it is the issue's own project. Otherwise the
 * project that came with the issue is used when it carries the attributes.
 */
export function resolveIssueWorkflowProject(
  issue: IssueWorkflowIssue | null | undefined,
  storeProject: IssueWorkflowProject | null | undefined,
  contextProjectId?: string | null
): IssueWorkflowProject | null {
  if (!issue) {
    return null;
  }
  if (issueBelongsToProject(issue, storeProject, contextProjectId)) {
    return storeProject;
  }
  const own = issue.project;
  if (own && typeof own === 'object' && 'source_entity_extra_attributes' in own) {
    return own;
  }
  return null;
}

export function isIssueWorkflowEnabledForIssue(
  issue: IssueWorkflowIssue | null | undefined,
  storeProject: IssueWorkflowProject | null | undefined,
  contextProjectId?: string | null
): boolean {
  return isIssueWorkflowEnabled(resolveIssueWorkflowProject(issue, storeProject, contextProjectId));
}

/** "sent_to_contractor" -> "Sent to contractor", for a value the API gave no label for. */
export function humanizeWorkflowValue(value: unknown): string {
  const text = String(value ?? '').replace(/[_-]+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/**
 * The status pill of an issue detail, or null when the issue has no status.
 * A status the web has no wording for still gets a pill.
 */
export function issueStatusChip(issue: IssueWorkflowIssue | null | undefined): IssueStatusChip | null {
  const status = issue?.status;
  if (!status) {
    return null;
  }
  const known = ISSUE_STATUS_CHIPS[status];
  return known
    ? { status, ...known }
    : { status, label: humanizeWorkflowValue(status), tone: 'neutral' };
}

/**
 * The pill of an issue row / card for its contractor-workflow stage, or null
 * for an open or closed issue: those keep the plain open / closed pill the
 * row always had, so a project without the workflow looks the same as before.
 */
export function issueStageChip(issue: IssueWorkflowIssue | null | undefined): IssueStatusChip | null {
  return issueStage(issue) ? issueStatusChip(issue) : null;
}

/**
 * Whether the legacy Close / Reopen controls are offered. On a workflow
 * project closing and reopening are workflow actions (project manager / org
 * admin, or the inspector who created the issue; recorded in the history), so
 * the legacy controls are not shown at all: one path per project mode.
 */
export function showLegacyIssueStatusControls(enabled: boolean): boolean {
  return !enabled;
}

/**
 * Whether an existing issue is editable. The legacy check also accepts
 * `create`; on a workflow project a contractor PM holds `create` for the
 * project but `update` only on the issues they raised, so there the answer is
 * the `update` ability alone.
 */
export function canEditExistingIssue(enabled: boolean, canUpdate: boolean, canCreate: boolean): boolean {
  return enabled ? canUpdate : canUpdate || canCreate;
}

/** A person on the issue's "Forwarded to" list, with when and by whom. */
export interface IssueAssignee {
  person: IssueWorkflowPerson;
  assigned_at: string | null;
  assigned_by_name: string | null;
}

/**
 * Everyone the issue is assigned to, each person once: the contractor
 * recipients (send) then the sub-contractor users (forward). A person on
 * both lists keeps the first assignment's date and author.
 */
export function issueAssignees(
  contractorAccounts: IssueWorkflowPerson[] | null | undefined,
  subAccounts: IssueWorkflowPerson[] | null | undefined,
): IssueAssignee[] {
  const assignees: IssueAssignee[] = [];
  const seen = new Set<string>();
  const add = (person: IssueWorkflowPerson, at: string | null | undefined, by: string | null | undefined) => {
    const key = person?.id || person?.email || workflowPersonName(person);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    assignees.push({ person, assigned_at: at || null, assigned_by_name: by || null });
  };
  (contractorAccounts || []).forEach(person => add(person, person?.sent_at, person?.sent_by_name));
  (subAccounts || []).forEach(person => add(person, person?.forwarded_at, person?.forwarded_by_name));
  return assignees;
}

export function workflowPersonName(person: IssueWorkflowPerson | null | undefined): string {
  return [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim() || person?.email || '';
}

/**
 * "Jane Doe (Acme Concrete)"; someone with no contractor shows their project
 * role ("Jane Doe (Project Manager)"); the email stands in for anyone with neither.
 */
export function workflowPersonLabel(person: IssueWorkflowPerson | null | undefined): string {
  const name = workflowPersonName(person);
  const detail = person?.sub_contractor_name || person?.role_name || (person?.email !== name ? person?.email : '');
  return detail ? `${name} (${detail})` : name;
}

/**
 * The wording the web falls back on when next-state gives an action or one of
 * its inputs no label. The API's labels always win: the send action is
 * "Forward" there, its recipients input "Forward to".
 */
const ISSUE_WORKFLOW_FALLBACK_LABELS: Record<string, { label: string; inputs?: Record<string, string> }> = {
  [ISSUE_WORKFLOW_STATES.send]: { label: 'Forward', inputs: { account_ids: 'Forward to' } },
  [ISSUE_WORKFLOW_STATES.forward]: { label: 'Forward', inputs: { account_ids: 'Forward to' } },
};

/** The label of an offered action: the API's, else the web's fallback, else the state. */
export function workflowActionLabel(action: WorkflowNextAction | null | undefined): string {
  return action?.options?.label || ISSUE_WORKFLOW_FALLBACK_LABELS[action?.state]?.label || action?.state || '';
}

/** The label of one input of an action: the API's, else the web's fallback, else its name. */
export function workflowInputLabel(
  action: WorkflowNextAction | null | undefined,
  input: WorkflowFormInput | null | undefined
): string {
  return input?.label
    || ISSUE_WORKFLOW_FALLBACK_LABELS[action?.state]?.inputs?.[input?.name]
    || humanizeWorkflowValue(input?.name);
}

/** Picker options for the people of GET /v1/issue/:id/contractor-users. */
export function workflowPersonOptions(
  people: IssueWorkflowPerson[] | null | undefined,
  excludeId?: string | null
): WorkflowInputOption[] {
  return (people || [])
    .filter(person => !!person?.id && person.id !== excludeId)
    .map(person => ({ value: String(person.id), label: workflowPersonLabel(person) }));
}

/** The distinct contractors of those users, in the order the API listed them. */
export function workflowSubContractorOptions(
  people: IssueWorkflowPerson[] | null | undefined,
  excludeId?: string | null
): WorkflowInputOption[] {
  const options: WorkflowInputOption[] = [];
  (people || []).forEach(person => {
    if (!person?.sub_contractor_id || person.id === excludeId) {
      return;
    }
    if (!options.some(option => option.value === person.sub_contractor_id)) {
      options.push({
        value: person.sub_contractor_id,
        label: person.sub_contractor_name || person.sub_contractor_id,
      });
    }
  });
  return options;
}

/**
 * The users offered once a contractor is picked: its own users, plus the users
 * linked to no contractor at all (picking the contractor is how the API is
 * told where those belong).
 */
export function workflowPeopleOfSubContractor(
  people: IssueWorkflowPerson[] | null | undefined,
  subContractorId: string | null | undefined
): IssueWorkflowPerson[] {
  const list = people || [];
  if (!subContractorId) {
    return list;
  }
  return list.filter(person => !person?.sub_contractor_id || person.sub_contractor_id === subContractorId);
}

/** The options of a form input as the API listed them. */
export function workflowInputOptions(input: WorkflowFormInput | null | undefined): WorkflowInputOption[] {
  return (input?.options || [])
    .filter(option => option?.value !== undefined && option?.value !== null)
    .map(option => ({
      value: String(option.value),
      label: option.label || humanizeWorkflowValue(option.value),
    }));
}

/** The API's label for a value of one of its choice inputs. */
export function workflowOptionLabel(input: WorkflowFormInput | null | undefined, value: unknown): string {
  const match = workflowInputOptions(input).find(option => option.value === String(value));
  return match ? match.label : humanizeWorkflowValue(value);
}

/**
 * How the action form renders an input: by its `type`, and as a picker
 * whenever there are options for it — the API's own (`select`) or the ones the
 * host supplies for an input the API leaves open (the people pickers).
 */
export function workflowInputKind(input: WorkflowFormInput, hasOptions: boolean): WorkflowInputKind {
  const type = input?.type || 'text';
  if (type === 'file[]') {
    return 'files';
  }
  const multiple = type.endsWith('[]');
  if (hasOptions || type === 'select') {
    return multiple ? 'multi-choice' : 'choice';
  }
  return multiple ? 'unsupported' : 'text';
}

/**
 * The `formValues` of a workflow execute call, built from the action's own
 * `formInputs`: nothing is sent that the state did not ask for. Text is sent
 * as typed ('' when empty, which is how the legacy close stores an empty
 * closing comment); an empty optional choice is left out so the API applies
 * its default.
 */
export function buildWorkflowFormValues(
  formInputs: WorkflowFormInput[] | null | undefined,
  values: Record<string, unknown> | null | undefined,
  hasOptions: (name: string) => boolean = () => false
): Record<string, unknown> {
  const formValues: Record<string, unknown> = {};
  (formInputs || []).forEach(input => {
    const value = values?.[input.name];
    switch (workflowInputKind(input, hasOptions(input.name))) {
      case 'files':
      case 'multi-choice':
        formValues[input.name] = Array.isArray(value) ? value : [];
        break;
      case 'choice':
        if (value !== undefined && value !== null && value !== '') {
          formValues[input.name] = value;
        }
        break;
      case 'text':
        formValues[input.name] = typeof value === 'string' ? value : '';
        break;
      default:
    }
  });
  return formValues;
}

function lineageFormValues(step: WorkflowLineageStep): Record<string, unknown> {
  const formValues = step?.input?.formValues;
  // Some clients send formValues as the bare comment string
  if (typeof formValues === 'string') {
    return formValues.trim() ? { comments: formValues } : {};
  }
  return formValues || {};
}

function lineageValue(step: WorkflowLineageStep, name: string): unknown {
  // The action's result carries the value it applied, the input what the
  // user sent.
  return step?.output?.[name] ?? lineageFormValues(step)[name];
}

function lineageNames(step: WorkflowLineageStep): string[] {
  // send: output.recipients, forward: output.sub_accounts — both [{ id, name }]
  return [step?.output?.recipients, step?.output?.sub_accounts]
    .reduce<{ name?: string | null }[]>((all, list) => (Array.isArray(list) ? all.concat(list) : all), [])
    .map(recipient => recipient?.name)
    .filter(Boolean);
}

function initials(actor: WorkflowLineageStep['actor']): string {
  if (!actor) {
    return '?';
  }
  return `${(actor.first_name || '')[0] || ''}${(actor.last_name || '')[0] || ''}`.toUpperCase() || '?';
}

function newestFirst(steps: WorkflowLineageStep[] | null | undefined): WorkflowLineageStep[] {
  return [...(steps || [])].sort((a, b) => (
    new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
  ));
}

/**
 * The issue's history as Discussion entries, newest first. Titles, choice
 * labels and recipient names all come from the lineage itself.
 */
export function issueWorkflowHistory(steps: WorkflowLineageStep[] | null | undefined): WorkflowDiscussionEntry[] {
  return newestFirst(steps).map(step => {
    const formValues = lineageFormValues(step);
    const stateName = step?.state?.name || '';
    const details = (step?.state?.params?.formInputs || [])
      .filter(input => Array.isArray(input?.options) && input.options.length > 0)
      .map(input => {
        const value = lineageValue(step, input.name);
        return value === undefined || value === null || value === ''
          ? null
          : { label: input.label || humanizeWorkflowValue(input.name), value: workflowOptionLabel(input, value) };
      })
      .filter(Boolean);
    const notes = formValues.comments;
    return {
      action: step?.state?.comment || step?.state?.params?.label || stateName || 'Action',
      state_name: stateName,
      actor_name: workflowPersonName(step?.actor) || 'Unknown',
      actor_initials: initials(step?.actor),
      date: step?.created_at || null,
      notes: typeof notes === 'string' && notes.trim() ? notes : null,
      attachments: step?.attachments || [],
      forwarded_to: lineageNames(step),
      details,
    };
  });
}
